import React from "react";

import _ from "lodash";

import { formatNumericCurrency } from "utils";

import { ACCOUNTING_METHODOLOGY, CarbonProgram, ENROLLMENT_TYPE, SubmissionDates, ValidationRules } from "models";

import { ListCardDataPair } from "components";

import FormBonusesTable from "pages/admin/carbonPrograms/details/FormBonusesTable";
import PayoutsTable from "pages/admin/carbonPrograms/details/PayoutsTable";
import PracticesTable from "pages/admin/carbonPrograms/details/PracticesTable";

// Every row mirrors one CarbonProgram field. When a field is not configured the row shows what the platform falls back
// to (marked `isDefault`), so these fallbacks must be kept in line with the code that applies them.

export interface ProgramLookups {
  cropNames: Record<string, string>;
  partnerName?: string;
}

const setting = <T,>(label: string, tooltip: string, configured: T | undefined, fallback: React.ReactNode, format: (value: T) => React.ReactNode = String) =>
  configured === undefined ? { label, tooltip, value: fallback, isDefault: true } : { label, tooltip, value: format(configured) };

const onOff = (value: boolean) => (value ? "On" : "Off");
const flag = (label: string, tooltip: string, value: boolean | undefined, fallback = false) => setting(label, tooltip, value, onOff(fallback), onOff);
const listOrNone = (items: string[]) => items.join(", ") || "None";
const cropList = (cropNames: Record<string, string>) => (cropIds: string[]) => listOrNone(cropIds.map((id) => cropNames[id] ?? id));

export const formatSubmissionDates = ({ start, due, end }: SubmissionDates, formatMonthDay: (monthDay: string) => string = String) =>
  [
    ["Start", start],
    ["Due", due],
    ["End", end],
  ]
    .map(([label, monthDay]) => `${label} ${monthDay ? formatMonthDay(monthDay) : "default"}`)
    .join(" · ");

const METHODOLOGY_NAMES: Record<string, string> = { klim: "Klim", cfp: "Cool Farm Tool (CFP)", lfl: "LfL" };
const methodologyName = (methodology: string) => METHODOLOGY_NAMES[methodology] ?? methodology;

const VALIDATION_RULE_NAMES: Record<keyof ValidationRules, string> = {
  attributeEntriesByDate: "Assign entries to years by harvest / seeding date",
  includeManagementFromSoilInitStart: "Include management from the soil model's start year",
  includeCarriedOverCrops: "Include crops sown before the first accounted year",
  exemptUpcomingYearFromDependencies: "Skip the practice dependency check for the upcoming year",
};

export const programRows = (program: CarbonProgram, { partnerName }: ProgramLookups): ListCardDataPair[] => [
  { label: "Name", tooltip: "Name farmers see", value: program.programName },
  setting("Internal name", "Name only shown in admin, to tell apart programs farmers see under the same name", program.internalName, program.programName),
  { label: "Slug", tooltip: "Short key used for feature flag targeting (FeatBit) and the program info popup", value: program.programSlug },
  { label: "ID", tooltip: "Unique program ID", value: program.id },
  {
    label: "Country",
    tooltip: "Sets practice management parameters (input guidance) and emission factors (footprint calculations) for this program's farms",
    value: program.country,
  },
  { label: "Currency", tooltip: "Currency all payouts are shown and paid in", value: program.currency },
  {
    label: "Accounting methodology",
    tooltip: "Intervention pays for practice changes against a baseline; inventory pays for the farm's yearly carbon balance",
    value: _.upperFirst(program.accountingMethodology),
  },
  { label: "Partner", tooltip: "Company funding the program (insetting); none means offsetting", value: partnerName ?? "None", isDefault: !program.partnerId },
  setting("Program terms", "Whether farms must accept a T&C contract before joining", program.noProgramTerms, "T&C contract required", (noTerms) =>
    noTerms ? "No contract, farms join without consent" : "T&C contract required"
  ),
  setting(
    "Verification",
    "Whether farms go through verification and must provide documents before submitting",
    program.verificationRequired,
    "Not verified",
    (verified) => (verified ? "Verified, documents required on submission" : "Not verified")
  ),
];

export const platformFeatureRows = ({ platformFeatures }: CarbonProgram) => ({
  fieldManagement: [
    flag("All fields view", "The fields pages, where farmers manage fields, crops and practices", platformFeatures.fieldManagement?.enabled),
    flag("Wallet / payouts", "Wallet entry in the navigation, and payout amounts in the inventory impact panel", platformFeatures.fieldManagement?.wallet),
    flag("Outcome based incentives", "Inventory only: shows payouts for the farm's carbon outcome", platformFeatures.fieldManagement?.outcomeBasedIncentives),
    flag("Practice based incentives", "Inventory only: shows payouts for individual practices", platformFeatures.fieldManagement?.practiceBasedIncentives),
    flag("Impact panel", "Impact panel next to the fields list", platformFeatures.fieldManagement?.impactPanel),
    flag(
      "Insights impact panel",
      "Shows the SoilSight insights panel instead of the default impact panel",
      platformFeatures.fieldManagement?.insightsImpactPanel
    ),
    flag("Submission", "Yearly data submission bar at the bottom of the fields pages", platformFeatures.fieldManagement?.submission),
  ],
  footprinting: [
    flag("Footprinting", "The footprinting pages, where farmers fill in farm, crop and livestock forms", platformFeatures.footprinting?.enabled),
    flag("Results page", "Footprint results page", platformFeatures.footprinting?.results, true),
    flag(
      "Payout information",
      "Shows form completion payouts to the farmer (does not affect whether they are paid)",
      platformFeatures.footprinting?.payouts,
      true
    ),
    flag("Dairy", "Dairy forms", platformFeatures.footprinting?.dairy),
    flag("Beef", "Beef forms", platformFeatures.footprinting?.beef),
    flag("Pork", "Pork and piglet forms", platformFeatures.footprinting?.pork),
  ],
  planning: [
    flag(
      "Crop mix composer",
      "Planning tool for crop rotations. When not set, the navigation hides it but the home page still shows its card",
      platformFeatures.planning?.cropMixComposer
    ),
  ],
});

export const fieldsManagementRows = (program: CarbonProgram, { cropNames }: ProgramLookups): ListCardDataPair[] => {
  const { fieldsManagement, currency } = program;

  return [
    setting("Start year", "First year farmers can submit data and receive payouts", fieldsManagement.startYear, "Immediately"),
    { label: "SOC model", tooltip: "Engine used to calculate soil organic carbon", value: fieldsManagement.methodology.socModel },
    { label: "SOC model version", tooltip: "Version of the SOC model sent with every calculation", value: fieldsManagement.methodology.version },
    setting(
      "Reporting period",
      "The yearly window farm data is collected and credited for",
      fieldsManagement.reportingPeriod,
      "01/01 - 12/31",
      ({ start, end }) => `${start} - ${end}`
    ),
    setting(
      "Submission dates",
      "Program-specific submission window (MM/DD). Each date that isn't set uses the platform default (see the Carbon Program Dates tab)",
      fieldsManagement.submissionDates,
      "Platform default",
      formatSubmissionDates
    ),
    setting(
      "Monitoring period",
      "Calendar year, or harvest year (adds a baseline year and requires the following year's data)",
      fieldsManagement.monitoringPeriodType,
      "Calendar year",
      (type) => (type === "harvest" ? "Harvest year" : "Calendar year")
    ),
    setting(
      "Field enrollment",
      "Whether fields join the program automatically or the farmer enrolls each one",
      fieldsManagement.fieldEnrollmentType,
      "Farmer enrolls fields manually",
      (type) => (type === ENROLLMENT_TYPE.auto ? "Automatic, all fields enrolled" : "Farmer enrolls fields manually")
    ),
    setting(
      "Program crops",
      "Crops incentivised by the program (not currently used by the platform)",
      fieldsManagement.programCrops,
      "None",
      cropList(cropNames)
    ),
    setting(
      "Crops of interest",
      "Inventory only: crops counted towards the project area and practice incentives",
      fieldsManagement.projectArea?.cropsOfInterest,
      "None",
      cropList(cropNames)
    ),
    setting("Maximum payout per hectare", "Cap on the yearly payout per hectare", fieldsManagement.maximumPayoutPerHa, "No cap", (max) =>
      max === null ? "No cap, potential payout hidden" : `${formatNumericCurrency(max, { currency })} / ha`
    ),
    setting("Validation rules", "Opt-in adjustments to the yearly practice validation", fieldsManagement.validationRules, "None", (rules) =>
      listOrNone((_.keys(_.pickBy(rules)) as (keyof ValidationRules)[]).map((rule) => VALIDATION_RULE_NAMES[rule]))
    ),
    flag("Precise tillage depth", "Tillage depth is entered as an exact number instead of a range", fieldsManagement.requirePreciseTillageDepth),
    flag("Tillage soil mixing", "Asks for the soil mixing type of each tillage", fieldsManagement.requireTillageSoilMixing),
    {
      ...setting("Credit prices", "What farmers are paid per kg CO₂, per type of credit", fieldsManagement.payouts, "Not set", () => "Per credit type"),
      children: fieldsManagement.payouts && <PayoutsTable payouts={fieldsManagement.payouts} currency={currency} />,
    },
    {
      label: "Practices",
      tooltip:
        program.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory
          ? "How each practice is validated, and the per hectare incentives paid for it"
          : "How each practice is validated: what is required, what it depends on, and how many baseline years are needed",
      value: `${fieldsManagement.practices.length} practices`,
      children: <PracticesTable practices={fieldsManagement.practices} currency={currency} />,
    },
  ];
};

export const farmLevelFootprintingRows = (program: CarbonProgram, { cropNames }: ProgramLookups): ListCardDataPair[] => {
  const { farmLevelFootprinting, currency } = program;
  const { methodology, formSubmissionBonuses } = farmLevelFootprinting;

  return [
    { label: "Start year", tooltip: "First year farmers can fill in footprinting forms", value: String(farmLevelFootprinting.startYear) },
    setting(
      "Verification start year",
      "First year footprints are verified. When not set, no verification tasks are assigned but payouts are not restricted by year",
      farmLevelFootprinting.verificationStartYear,
      "Not set"
    ),
    setting("Disabled years", "Years between the start year and now without data collection", farmLevelFootprinting.disabledYears, "None", (years) =>
      listOrNone(years.map(String))
    ),
    { label: "Program crops", tooltip: "Crops with a footprinting form on the landing page", value: cropList(cropNames)(farmLevelFootprinting.programCrops) },
    setting("Emission factors version", "Version of the emission factors used for calculations", methodology?.version, "Latest published"),
    setting("Crop methodology", "Calculation engine for crop footprints", methodology?.crops, methodologyName("klim"), methodologyName),
    setting("Dairy methodology", "Calculation engine for dairy footprints", methodology?.dairy, methodologyName("lfl"), methodologyName),
    setting("Beef methodology", "Calculation engine for beef footprints", methodology?.beef, methodologyName("lfl"), methodologyName),
    setting("Pork methodology", "Calculation engine for pork and piglet footprints", methodology?.pork, methodologyName("lfl"), methodologyName),
    setting(
      "Hidden form steps",
      "Form steps removed from the flow and from the completion percentage",
      farmLevelFootprinting.excludedFormSteps,
      "None",
      listOrNone
    ),
    {
      label: "Form completion bonuses",
      tooltip: "Paid per year once forms are completed and verified",
      value: formSubmissionBonuses.cropPayouts === "per-crop" ? "Crop bonus paid per crop form" : "Crop bonus paid once all crop forms are complete",
      children: <FormBonusesTable formSubmissionBonuses={formSubmissionBonuses} currency={currency} />,
    },
  ];
};
